'use client'

import { useEffect, useRef } from 'react'

import { cn } from '@/lib/utils'
import { HalloweenLogo } from '@/components/halloween'
import { LogoCentreStackedInline } from '@/components/logo-centre-stacked-inline'
import { LogoLeftStackedInline } from '@/components/logo-left-stacked-inline'
import { LogoWideInline } from '@/components/logo-wide-inline'

type Variant = 'wide' | 'centre-stacked' | 'left-stacked'

interface NeonLogoProps {
  variant: Variant
  className?: string
  /** Animate immediately on mount (above-fold header) */
  priority?: boolean
  colour?: 'brand' | 'moonglow' | 'slime'
  /** External trigger, animate when this changes to true */
  trigger?: boolean
}

const seasonalRatios: Record<Variant, string> = {
  wide: '582 / 149',
  'centre-stacked': '396.5 / 285',
  'left-stacked': '330 / 198',
}

const logos: Record<Variant, React.ComponentType<{ className?: string }>> = {
  wide: LogoWideInline,
  'centre-stacked': LogoCentreStackedInline,
  'left-stacked': LogoLeftStackedInline,
}

export function NeonLogo({
  variant,
  className = '',
  priority = false,
  colour = 'moonglow',
  trigger: externalTrigger,
}: NeonLogoProps) {
  const ref = useRef<HTMLDivElement>(null)
  const firedRef = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el || firedRef.current) return

    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced) {
      firedRef.current = true
      return
    }

    function fire() {
      if (!el || firedRef.current) return
      firedRef.current = true
      el.classList.add('logo-neon-on')
    }

    // Priority: animate on mount
    if (priority) {
      requestAnimationFrame(fire)
      return
    }

    // External trigger (e.g. sheet open)
    if (externalTrigger) {
      requestAnimationFrame(fire)
      return
    }

    // Default: IntersectionObserver
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          fire()
          observer.unobserve(el)
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [priority, externalTrigger])

  const Logo = logos[variant]

  return (
    <div
      ref={ref}
      className={cn('logo-neon-ready spoopy-logo inline-flex', className)}
      style={{ aspectRatio: seasonalRatios[variant] }}
    >
      <Logo className="hidden" />
      <HalloweenLogo
        colour={colour}
        format={
          variant === 'wide'
            ? 'hero-linear'
            : variant === 'centre-stacked'
              ? 'hero-stacked'
              : 'hero-alt-stack'
        }
      />
      <span className="sr-only">Rock&apos;n Earthworks</span>
    </div>
  )
}
